import { api } from '@hunk/shared/api';
import type { CheckoutAddress } from '@hunk/shared/api/orders';
import { type DeliveryOption, deliveryFee, effectivePrice, shippingFor } from '@hunk/shared/api/shop';
import { type DeliveryMethodType, deliveryNeedsAddress, formatMoney, type PaymentOptionType } from '@hunk/shared/common';
import { Loader2, Lock, Mail, PackageCheck, ShieldCheck, Truck } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router';

import { IS_MINI_APP } from '@/appMode';
import { openPayment, PaymentPicker, usePaymentOptions } from '@/components/PaymentPicker';
import { ProductImage } from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { useCart } from '@/hooks/useCart';
import { errorText, useSession } from '@/hooks/useSession';
import { useShop } from '@/hooks/useShop';
import { cn } from '@/lib/utils';

const EMPTY: Required<CheckoutAddress> = { fullName: '', phone: '', email: '', city: '', street: '', houseNumber: '', apartment: '', zip: '', notes: '' };

/** details -> delivery (one of the shop's delivery options, discreet packaging) -> payment option -> the provider's page */
export function CheckoutPage() {
  const { status, session, authHeaders } = useSession();
  const { items, itemsTotal, lines, clear } = useCart();
  const { delivery, reload: reloadShop } = useShop();
  const navigate = useAppNavigate();
  const options = usePaymentOptions();
  const [form, setForm] = useState(EMPTY);
  const [optionId, setOptionId] = useState<string>();
  const [discreet, setDiscreet] = useState(false);
  const [option, setOption] = useState<PaymentOptionType>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  // pre-fill from the last order / the account
  useEffect(() => {
    if (status !== 'authenticated') return;
    api.orders
      .lastDetails({ headers: authHeaders() })
      .then((r) =>
        setForm((f) => ({
          ...f,
          ...Object.fromEntries(Object.entries(r.response.details ?? {}).filter(([, v]) => v)),
          email: r.response.details?.email || session?.user?.email || f.email
        }))
      )
      .catch(() => undefined);
  }, [status, authHeaders, session?.user?.email]);
  // the catalog is loaded once per visit - fetch the current delivery options / prices (the admin may have changed them)
  useEffect(() => void reloadShop(), [reloadShop]);
  useEffect(() => {
    if (!delivery || delivery.options.some((o) => o.id === optionId)) return;
    setOptionId(delivery.options[0]?.id);
  }, [delivery, optionId]);
  useEffect(() => {
    if (options?.length === 1) setOption(options[0]);
  }, [options]);

  if (status === 'anonymous') return <Navigate to="/login?next=/checkout" replace />;
  if (status === 'loading' || !delivery) return <Spinner />;
  if (!items.length) return <Navigate to="/cart" replace />;

  const selected = delivery.options.find((o) => o.id === optionId);
  const shipping = selected ? shippingFor(delivery, selected, itemsTotal, discreet) : 0;
  const total = itemsTotal + shipping;
  const set = (k: keyof CheckoutAddress) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    if (!selected) return setError('בחרו אופן משלוח');
    if (!option) return setError('בחרו אמצעי תשלום');
    setBusy(true);
    try {
      const r = (
        await api.orders.checkout(
          {
            ...form,
            items: lines,
            deliveryOptionId: selected.id,
            discreet,
            paymentOption: option,
            app: IS_MINI_APP ? 'mini' : 'web'
          },
          { headers: authHeaders() }
        )
      ).response;
      clear(); // the order holds the items now (an unpaid order can be paid again from "ההזמנות שלי")
      openPayment(r, option, navigate);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-[1100px] px-3 py-6 md:px-4 md:py-10">
      <h1 className="mb-5 text-2xl font-extrabold">תשלום</h1>
      <form onSubmit={(e) => void submit(e)} className="grid gap-6 md:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-5">
          <Box title="פרטים">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="שם מלא" required>
                <Input value={form.fullName} onChange={set('fullName')} autoComplete="name" required maxLength={80} />
              </Field>
              <Field label="טלפון" required>
                <Input value={form.phone} onChange={set('phone')} autoComplete="tel" inputMode="tel" dir="ltr" required maxLength={20} />
              </Field>
              <Field label="אימייל (לאישור ההזמנה)">
                <Input value={form.email} onChange={set('email')} autoComplete="email" type="email" dir="ltr" maxLength={120} />
              </Field>
            </div>
          </Box>

          <Box title="משלוח">
            <div className="grid gap-2 sm:grid-cols-2">
              {delivery.options.map((o) => (
                <Choice
                  key={o.id}
                  active={optionId === o.id}
                  onClick={() => setOptionId(o.id)}
                  icon={<DeliveryIcon kind={o.kind} className="size-5" />}
                  title={o.name}
                >
                  <OptionPrice option={o} itemsTotal={itemsTotal} />
                  {o.description && <span className="block text-xs text-neutral-500">{o.description}</span>}
                </Choice>
              ))}
            </div>
            {!delivery.options.length && <p className="text-sm text-amber-400">אין כרגע אפשרויות משלוח זמינות</p>}
            {selected?.kind === 'post' && <p className="mt-3 text-xs text-neutral-400">החבילה תישלח לסניף הדואר / לוקר הקרוב לכתובת, ודואר ישראל יודיע לכם כשהיא מחכה.</p>}
            {selected && deliveryNeedsAddress(selected.kind) && (
              <div className="mt-3 grid gap-3 sm:grid-cols-6">
                <Field label="עיר" required className="sm:col-span-3">
                  <Input value={form.city} onChange={set('city')} autoComplete="address-level2" required maxLength={60} />
                </Field>
                <Field label="מיקוד" className="sm:col-span-3">
                  <Input value={form.zip} onChange={set('zip')} autoComplete="postal-code" inputMode="numeric" dir="ltr" maxLength={10} />
                </Field>
                <Field label="רחוב" required className="sm:col-span-4">
                  <Input value={form.street} onChange={set('street')} autoComplete="address-line1" required maxLength={80} />
                </Field>
                <Field label="מספר בית" required className="sm:col-span-1">
                  <Input value={form.houseNumber} onChange={set('houseNumber')} required maxLength={10} />
                </Field>
                <Field label="דירה" className="sm:col-span-1">
                  <Input value={form.apartment} onChange={set('apartment')} maxLength={20} />
                </Field>
              </div>
            )}
            <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 p-3">
              <input type="checkbox" checked={discreet} onChange={(e) => setDiscreet(e.target.checked)} className="accent-primary mt-1 size-4" />
              <ShieldCheck className="text-primary mt-0.5 size-5 shrink-0" />
              <span className="text-sm">
                <span className="font-semibold">אריזה דיסקרטית{delivery.discreetFee ? ` (+${formatMoney(delivery.discreetFee)})` : ''}</span>
                <span className="block text-xs text-neutral-400">חבילה אטומה, בלי שם החנות ובלי פירוט המוצרים</span>
              </span>
            </label>
            <Field label="הערות להזמנה" className="mt-3">
              <textarea
                value={form.notes}
                onChange={set('notes')}
                maxLength={500}
                rows={2}
                className="border-input w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-white/60"
              />
            </Field>
          </Box>

          <Box title="אמצעי תשלום">{options ? <PaymentPicker options={options} value={option} onChange={setOption} /> : <Spinner small />}</Box>
        </div>

        <aside className="h-fit rounded-2xl border border-white/10 bg-neutral-950 p-4 md:sticky md:top-28">
          <h2 className="mb-3 font-bold">ההזמנה</h2>
          <ul className="mb-3 flex max-h-64 flex-col gap-2 overflow-y-auto">
            {items.map(({ product: p, qty }) => (
              <li key={p.id} className="flex items-center gap-2 text-sm">
                <span className="size-10 shrink-0 overflow-hidden rounded-md bg-white p-0.5">
                  <ProductImage src={p.images[0]} alt="" />
                </span>
                <span className="line-clamp-2 min-w-0 flex-1">
                  {p.name} <span className="text-neutral-500">× {qty}</span>
                </span>
                <span className="font-semibold whitespace-nowrap">{formatMoney(effectivePrice(p) * qty)}</span>
              </li>
            ))}
          </ul>
          <dl className="flex flex-col gap-1.5 border-t border-white/10 pt-3 text-sm">
            <Row label="מוצרים" value={formatMoney(itemsTotal)} />
            <Row label={selected?.name ?? 'משלוח'} value={shipping ? formatMoney(shipping) : 'חינם'} />
            <div className="mt-1 flex justify-between text-lg font-extrabold">
              <dt>סה"כ לתשלום</dt>
              <dd>{formatMoney(total)}</dd>
            </div>
          </dl>
          {error && <p className="mt-3 rounded-lg bg-red-500/10 p-2 text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={busy || !option || !selected}
            className="brand-grad brand-grad-hover mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 text-base font-bold text-white disabled:opacity-50"
          >
            {busy ? <Loader2 className="size-5 animate-spin" /> : <Lock className="size-4" />}
            לתשלום {formatMoney(total)}
          </button>
          <p className="mt-2 text-center text-xs text-neutral-500">התשלום מתבצע בדף מאובטח של חברת הסליקה</p>
        </aside>
      </form>
    </main>
  );
}

function Box({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-neutral-950 p-4">
      <h2 className="mb-3 font-bold">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label>
        {label}
        {required && <span className="text-primary">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Choice({ active, onClick, icon, title, children }: { active: boolean; onClick: () => void; icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('flex items-start gap-3 rounded-xl border p-3 text-start transition-colors', active ? 'border-primary bg-primary/10' : 'border-white/10 hover:bg-white/5')}
    >
      <span className={cn('mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2', active ? 'border-primary' : 'border-neutral-500')}>
        {active && <span className="bg-primary size-2 rounded-full" />}
      </span>
      {icon && <span className="text-primary shrink-0">{icon}</span>}
      <span className="text-sm">
        <span className="block font-semibold">{title}</span>
        <span className="text-neutral-400">{children}</span>
      </span>
    </button>
  );
}

export function DeliveryIcon({ kind, className }: { kind: DeliveryMethodType; className?: string }) {
  const Icon = kind === 'pickup' ? PackageCheck : kind === 'post' ? Mail : Truck;
  return <Icon className={className} />;
}

/** "₪30 · חינם מעל ₪300" / "חינם" */
export function OptionPrice({ option, itemsTotal }: { option: DeliveryOption; itemsTotal: number }) {
  const fee = deliveryFee(option, itemsTotal);
  return (
    <>
      {fee ? formatMoney(fee) : 'חינם'}
      {fee > 0 && option.freeFrom != null ? ` · חינם מעל ${formatMoney(option.freeFrom)}` : ''}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-neutral-300">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export function Spinner({ small }: { small?: boolean }) {
  return (
    <div className={cn('flex justify-center', small ? 'py-2' : 'py-24')}>
      <Loader2 className={cn('text-primary animate-spin', small ? 'size-5' : 'size-10')} />
    </div>
  );
}
